
import React, { useEffect, useState } from "react";
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend 
} from "recharts";
import { GlassCard } from "@/components/ui/glass-card";
import { type ChartType, playerColors } from "./chart-data";

interface ChartDisplayProps {
  chartData: any[];
  chartType: ChartType;
}

export function ChartDisplay({ chartData, chartType }: ChartDisplayProps) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    // Initial check
    checkDarkMode();
    
    // Set up observer to detect theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' && 
            mutation.target === document.documentElement) {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <GlassCard>
      <div className="h-64 p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={isDarkMode ? "#444" : "#d4d4d8"} 
              opacity={isDarkMode ? 0.4 : 0.3}
            />
            <XAxis 
              dataKey="name" 
              tick={{ 
                fontSize: 12, 
                fill: isDarkMode ? "#e0e0e0" : "#888"
              }} 
            />
            <YAxis 
              tick={{ 
                fontSize: 12, 
                fill: isDarkMode ? "#e0e0e0" : "#888"
              }}
              domain={[0, chartType === "line" ? 3 : 'auto']}
              allowDecimals={false}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: isDarkMode ? "rgba(30, 30, 40, 0.9)" : "rgba(255, 255, 255, 0.8)",
                borderRadius: "8px",
                border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                color: isDarkMode ? "#ffffff" : undefined
              }}
              formatter={(value, name) => [
                `${value} ${chartType === "cumulative" ? 'total points' : 'points'}`, 
                name
              ]}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            
            {/* Only show top 3 players for clarity */}
            <Line
              type="monotone"
              dataKey="Player1"
              stroke={playerColors.Player1}
              strokeWidth={isDarkMode ? 2.5 : 2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              name="Alex"
            />
            <Line
              type="monotone"
              dataKey="Player2"
              stroke={playerColors.Player2}
              strokeWidth={isDarkMode ? 2.5 : 2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              name="Sam"
            />
            <Line
              type="monotone"
              dataKey="Player3"
              stroke={playerColors.Player3}
              strokeWidth={isDarkMode ? 2.5 : 2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              name="Jordan"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </GlassCard>
  );
}
